import { CSSProperties, ReactNode, forwardRef } from "react";
import { SxProps } from '@mui/system';
import BTButton from "@common-components/bt-button/bt-button";

interface BTbotbuttonProps {
    disabled?: boolean;
    styleType?: 'full' | 'empty' | 'dashed';
    size?: 'small' | 'medium' | 'large';
    onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
    children: ReactNode;
    sx?: SxProps;
    style?: CSSProperties;
    className?: string;
    startIcon?: ReactNode;
    type?: 'button' | 'submit' | 'reset';
    loading?: boolean;
}

// Per-size overrides on top of BTButton's primary theme.
const SIZE_SX: Record<NonNullable<BTbotbuttonProps['size']>, Record<string, any>> = {
    small: {
        minHeight: '32px',
        padding: '4px 14px',
        fontSize: '12.5px',
    },
    medium: {
        minHeight: '38px',
        padding: '6px 18px',
        fontSize: '13px',
    },
    large: {
        minHeight: '46px',
        padding: '10px 22px',
        fontSize: '14px',
    },
};

// "empty" / "dashed" override the BTButton primary fill so we get a clean
// outlined button suitable for Cancel / icon actions / secondary CTAs while
// still going through the same Blueticks button component.
const OUTLINED_SX: Record<string, any> = {
    background: 'var(--color-surface, #ffffff) !important',
    color: 'var(--color-text, #1f2937) !important',
    border: '1px solid var(--color-border, #e2e8f0)',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    '&:hover': {
        background: 'var(--color-surface-muted, #f8fafc) !important',
        borderColor: 'var(--color-text-muted, #94a3b8)',
        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
    },
    '&.MuiButton-root': {
        color: 'var(--color-text, #1f2937) !important',
    },
    '&.MuiButtonBase-root': {
        color: 'var(--color-text, #1f2937) !important',
    },
};

const DASHED_SX: Record<string, any> = {
    ...OUTLINED_SX,
    border: '1.5px dashed var(--color-border, #cbd5e1)',
};

const BTbotbutton = forwardRef<HTMLButtonElement, BTbotbuttonProps>(({
    styleType = 'full',
    size = 'medium',
    sx,
    children,
    ...rest
}, ref) => {
    const variantSx =
        styleType === 'empty' ? OUTLINED_SX
            : styleType === 'dashed' ? DASHED_SX
                : {};

    const mergedSx: Record<string, unknown> = {
        // Sit nicely next to other buttons in a row.
        textTransform: 'none',
        whiteSpace: 'nowrap',
        ...SIZE_SX[size],
        ...variantSx,
        ...((sx as Record<string, unknown>) || {}),
    };

    return (
        <BTButton
            ref={ref}
            btvariant="primary"
            sx={mergedSx}
            {...rest}
        >
            {children}
        </BTButton>
    );
});

BTbotbutton.displayName = 'BTbotbutton';

export default BTbotbutton;
